/**
 * Stage 0 (readerUids / Option A′) — the projection from a student's roster auth
 * allow-list to the `readerUids` written on each of their grade docs.
 *
 * Kept in its OWN file with ZERO imports so the firebase-admin netlify functions
 * (stamps, revocation fan-out, backfill reconcile) can import it without dragging
 * in React/type transitive deps — same rationale as studentPiiFields.ts.
 *
 * CRITICAL invariant: every caller OVERWRITES a grade doc's `readerUids` with the
 * result of this function (a `set`, never an `arrayUnion`). `authUids` in the
 * private roster is the sole source of truth; readerUids is a disposable projection.
 * A union would silently resurrect a uid a password reset removed from authUids and
 * break password-reset-as-device-revocation — the whole point of A′.
 */

// Today readers = the student's own device auth uids. Parents read released grades
// through server-mediated magic links (no persistent Firebase uid), so there is no
// parent uid to add yet; `extraReaders` is future-proofing for if that ever changes.
export const deriveReaders = (
  authUids: readonly string[] | undefined,
  extraReaders: readonly string[] = [],
): string[] => {
  const set = new Set<string>();
  for (const u of authUids || []) if (u) set.add(u);
  for (const u of extraReaders) if (u) set.add(u);
  return Array.from(set);
};

/**
 * Set-equality for two readerUids arrays — order-independent (D3). The reconcile's
 * entriesEqual MUST use this: comparing readerUids by array-order would judge a doc
 * with the same members in a different order as "changed" (churn), and — worse — a
 * plain length/index compare could miss a real membership difference. A reconcile
 * that can't detect a stale reader set can't heal it.
 */
export const sameReaderSet = (
  a: readonly string[] | undefined,
  b: readonly string[] | undefined,
): boolean => {
  const sa = new Set(a || []);
  const sb = new Set(b || []);
  if (sa.size !== sb.size) return false;
  for (const u of sa) if (!sb.has(u)) return false;
  return true;
};
